<script lang="ts" setup>
    import type {
        ChatMessageActions,
        MessageFeedback,
        RevisedAnswer,
        UIChatMessage,
    } from 'models'
    import PkStreamingMarkdown from './PkStreamingMarkdown.vue'
    import PkChatbotError from './PkChatbotError.vue'
    import PkChatbotFeedbackForm from './PkChatbotFeedbackForm.vue'
    import PkChatbotMessageFeedback from './PkChatbotMessageFeedback.vue'
    import PkChatbotFilePreview from './PkChatbotFilePreview.vue'
    import PkRelativeTime from '../PkRelativeTime.vue'
    import { useI18n } from 'vue-i18n'
    import {
        useTemplateRef,
        ref,
        watch,
        computed,
        nextTick,
        onMounted,
        useSlots,
    } from 'vue'
    import { toolComponentMap } from './toolComponentMap'
    import PkToolApprovalCard from './PkToolApprovalCard.vue'
    import PkChatbotSteps from './PkChatbotSteps.vue'
    import {
        resolveContrastColor,
        getPartState,
        isTextPart,
        isFilePart,
        isToolPart,
        isReasoningPart,
        isStreamingPart,
        isToolConfirmPart,
        isConfirmationCardPart,
        getToolApprovalId,
        getPartIcon,
        getToolPartLabel,
        mergeConsecutiveTextParts,
        formatDuration,
    } from './utils'
    import { getFilePartMedia, getToolPartName, toKebabCase } from 'utils'
    import PkStreamingMarkdownAutoscroll from './PkStreamingMarkdownAutoscroll.vue'
    import PkChatbotOutlineRail from './PkChatbotOutlineRail.vue'
    import { useChatScroll } from './useChatScroll'

    const props = defineProps<{
        status?: 'submitted' | 'streaming' | 'ready' | 'error'
        messages?: UIChatMessage[]
        error?: Error
        actions?: ChatMessageActions[]
        mainColor?: string
        textColor?: 'auto' | 'white' | 'black'
        revisedAnswers?: RevisedAnswer[]
        messageFeedbacks?: MessageFeedback[]
        /**
         * Render the vote/feedback action buttons as read-only state indicators
         * (e.g. admin chat-logs view): icons reflect the user's feedback but the
         * buttons are not interactive.
         */
        readonlyFeedback?: boolean
        disableHeightAdjustment?: boolean
        showMessageDateTime?: boolean
        showMessageTokensCount?: boolean
        showAllMessageParts?: boolean
        /**
         * Lets the user answer the tool requests rendered in the feed (approval
         * and confirm cards). Off by default: the log viewers render the same
         * messages read-only and have nothing to answer with.
         */
        interactive?: boolean
        showExtendedSteps?: boolean
        /**
         * A call is waiting for its answer. The turn runs outside this client
         * (the voice provider drives it), so `status` says nothing about it and
         * the feed would sit still for the seconds it takes.
         */
        awaitingSpokenAnswer?: boolean
        /**
         * Verdicts given ON A CALL, by toolCallId. A confirm answered during a
         * call goes back to the agent as a spoken message, so its part never
         * leaves the render-ack state: without this the card kept its buttons
         * after the answer, and every extra tap said "Confermo" again
         * (measured 2026-08-07). Only successful sends are recorded, so a tap
         * that never reached the provider leaves the card usable.
         */
        confirmVerdictsOnCall?: Map<string, boolean>
        isDark?: boolean
        /** Show a floating "back to bottom" button when the user scrolls up (fullscreen layout) */
        showScrollToBottom?: boolean
        // TODO: move feedback in a separate component to avoid passing these props
        feedbackMessageId?: string
        feedbackLoading?: boolean
        feedbackSubmitted?: boolean
        feedbackError?: string
    }>()

    const emit = defineEmits<{
        (e: 'show-info', message: UIChatMessage): void
        (e: 'regenerate'): void
        (e: 'revise', message: UIChatMessage): void
        (e: 'upvote', message: UIChatMessage): void
        (e: 'downvote', message: UIChatMessage): void
        (e: 'feedback', message: UIChatMessage): void
        (e: 'feedback-submit', comment: string): void
        (e: 'feedback-close'): void
        (e: 'scroll-up'): void
        (e: 'scroll-down'): void
        (e: 'auto-retry'): void
        (e: 'reset-chat'): void
        (
            e: 'approval-respond',
            payload: { id: string; approved: boolean },
        ): void
        (
            e: 'confirm-respond',
            payload: { toolCallId: string; confirmed: boolean },
        ): void
    }>()

    const {
        t: $t,
        d: $d,
        n: $n,
    } = useI18n({
        useScope: 'global',
    })

    const slots = useSlots()

    /**
     * Forwards the user's verdict from the confirmation card. The two kinds of
     * request travel back on different channels: an approval answers the SDK
     * approval it carries the id of, a `requestConfirm` resolves as a client
     * tool output. Without the id there is nothing to answer, so the click is
     * dropped instead of sending an unmatchable response.
     */
    const onCardRespond = (
        part: UIChatMessage['parts'][number],
        approved: boolean,
    ) => {
        if (isToolConfirmPart(part)) {
            const toolCallId = (part as { toolCallId?: string }).toolCallId
            if (toolCallId) {
                emit('confirm-respond', { toolCallId, confirmed: approved })
            }
            return
        }
        const id = getToolApprovalId(part)
        if (!id) {
            return
        }
        emit('approval-respond', { id, approved })
    }

    const scrollEl = useTemplateRef<HTMLDivElement>('scrollEl')
    const wrapperEl = useTemplateRef<HTMLDivElement>('wrapperEl')
    const contrastColor = computed(() =>
        resolveContrastColor(props.textColor, props.mainColor),
    )

    // --- Scroll ---

    const { handleScroll, scrollToBottom, userScrolledUp, isAtBottom } =
        useChatScroll({
            scrollEl,
            status: () => props.status,
            messagesLength: () => props.messages?.length,
            lastMessageRole: () =>
                props.messages?.[props.messages.length - 1]?.role,
            contentEl: () => wrapperEl.value,
            beforeNewMessageScroll: () => updateAnchorSpacer(),
            onScrollUp: () => emit('scroll-up'),
            onScrollDown: () => emit('scroll-down'),
        })

    const onScrollToBottomClick = () => {
        // scrollToBottom() is a no-op while userScrolledUp is set
        userScrolledUp.value = false
        scrollToBottom()
    }

    // --- Conversation outline (fullscreen, desktop) ---

    const outlineItems = computed(() =>
        (props.messages ?? [])
            .filter((message) => message.role === 'user')
            .map((message) => ({
                id: message.id,
                preview:
                    message.parts.find(isTextPart)?.text.trim().slice(0, 120) ||
                    '…',
            })),
    )
    const activeOutlineId = ref<string>()

    // The rail entries map 1:1 onto the rendered user messages
    const getUserMessageEls = () =>
        scrollEl.value?.querySelectorAll('.pk-chatbot-message--user') ?? []

    // Scroll-spy: active is the last user message above the middle of the
    // viewport (the exchange currently being read)
    const updateActiveOutline = () => {
        const scroller = scrollEl.value
        if (!scroller || !props.showScrollToBottom) {
            return
        }
        const line =
            scroller.getBoundingClientRect().top + scroller.clientHeight / 2
        let active = outlineItems.value[0]?.id
        getUserMessageEls().forEach((el, index) => {
            if (el.getBoundingClientRect().top <= line) {
                active = outlineItems.value[index]?.id
            }
        })
        activeOutlineId.value = active
    }

    watch(outlineItems, async () => {
        await nextTick()
        updateActiveOutline()
    })

    const onScroll = () => {
        handleScroll()
        updateActiveOutline()
    }

    const scrollToUserMessage = (id: string) => {
        const scroller = scrollEl.value
        const index = outlineItems.value.findIndex((item) => item.id === id)
        const messageEl = getUserMessageEls()[index]
        if (!scroller || !messageEl) {
            return
        }
        // Intentional jump away from the live tail: the wheel/touch intent
        // detection cannot see programmatic scrolls, pause the auto-follow
        // explicitly (reaching the bottom again re-enables it)
        userScrolledUp.value = true
        // Same resting line as a freshly sent message: top of the content area
        scroller.scrollTo({
            top:
                scroller.scrollTop +
                messageEl.getBoundingClientRect().top -
                scroller.getBoundingClientRect().top -
                Number.parseFloat(getComputedStyle(scroller).paddingTop),
            behavior: 'smooth',
        })
    }

    // --- Height adjustment ---

    const height = ref<number>(0)
    // The min-height transition only serves the release (see below): while the
    // spacer grows it would hide the new height from the anchor scroll, which
    // reads `scrollHeight` once and would land short of the anchor.
    const isSpacerSettling = ref(false)

    // Recalculate when a message is added or the conversation changes (the
    // store recreates `messages` on every streamed token, so watching the
    // array reference would re-run this on each character). The height only
    // depends on the last user message height, which is stable while
    // streaming. Idempotent: also called by the scroll composable right
    // before the anchor scroll, so the spacer is always in place first.
    const updateAnchorSpacer = async () => {
        const messages = props.messages
        if (!messages?.length || props.disableHeightAdjustment) {
            return
        }
        // The spacer only serves the in-flight exchange, letting the
        // fresh user message anchor at the top while the answer streams.
        // On plain loads (e.g. opening an old conversation) it would
        // just leave a large void after the last message.
        const isExchangeInFlight =
            messages[messages.length - 1].role === 'user' ||
            props.status === 'submitted' ||
            props.status === 'streaming'
        if (!isExchangeInFlight) {
            return
        }
        isSpacerSettling.value = false
        await nextTick()
        if (!scrollEl.value) {
            return
        }
        // Spacer sized so the freshly sent user message rests at the top
        // of the content area once scrolled to bottom: subtract both
        // paddings (clientHeight includes them) so any extra top
        // clearance (e.g. the fullscreen overlay header) is respected,
        // and the wrapper row gap separating the message from the spacer
        const { paddingTop, paddingBottom } = getComputedStyle(scrollEl.value)
        const scrollElHeight =
            scrollEl.value.clientHeight -
            Number.parseFloat(paddingTop) -
            Number.parseFloat(paddingBottom)
        const wrapperGap = wrapperEl.value
            ? Number.parseFloat(getComputedStyle(wrapperEl.value).rowGap) || 0
            : 0
        // Measured on the rendered DOM (same lookup as the outline rail):
        // indexing the template-refs array with the `messages` index breaks
        // as soon as a message renders nothing (`v-if` on empty parts), the
        // refs array skips it and the heights shift by one.
        const userMessageEls = getUserMessageEls()
        const lastUserMessageHeight =
            userMessageEls[userMessageEls.length - 1]?.clientHeight ?? 0
        const newHeight = scrollElHeight - lastUserMessageHeight - wrapperGap
        // A question taller than the content area leaves nothing to reserve:
        // the spacer goes to zero, never back to the value of the previous
        // exchange. A stale, larger spacer is the very failure the anchor
        // exists to prevent, the scroll landing past the message. Skipped
        // when the element measures nothing (rendered while hidden): there
        // the height already in place is still the good one.
        if (scrollElHeight > 0) {
            height.value = Math.max(newHeight, 0)
        }
        await nextTick()
    }

    watch(
        () => [
            props.messages?.length,
            props.messages?.[props.messages.length - 1]?.id,
        ],
        () => updateAnchorSpacer(),
    )

    // Mounted mid-send (fullscreen first message: the messages view replaces
    // the empty state after the message is already in): the length watch
    // never fires for that message, apply the anchor spacer now. A first
    // spoken line mounts the same way and never moves `status`, hence the
    // second guard. Plain history loads (mounted at 'ready', nothing in
    // flight) stay untouched.
    onMounted(() => {
        if (
            props.status === 'submitted' ||
            props.status === 'streaming' ||
            props.awaitingSpokenAnswer
        ) {
            updateAnchorSpacer()
        }
    })

    // Release the spacer once the exchange is over: it would otherwise
    // leave a large empty area after the last message. The min-height
    // transition settles the layout smoothly (the browser clamps the
    // scroll progressively when the viewport sat inside the removed area).
    watch(
        () => props.status,
        (status) => {
            if (status === 'ready' || status === 'error') {
                isSpacerSettling.value = true
                height.value = 0
            }
        },
    )

    const isRevised = (messageId: string) => {
        return props.revisedAnswers?.some((r) => r.messageId === messageId)
    }

    const getMessageFeedback = (messageId: string) =>
        props.messageFeedbacks?.find((f) => f.messageId === messageId)

    // Interactive vote/feedback buttons. In read-only mode (admin chat-logs)
    // the vote is shown on the message itself (coloured bubble border + an
    // "active" thumb in the footer) and the comment, if any, below it.
    const isFeedbackButtonVisible = (
        kind: 'upvote' | 'downvote' | 'feedback',
        index: number,
    ) => !props.readonlyFeedback && isActionButtonVisible(kind, index)

    // Read-only feedback for a message (drives the reaction chip anchored to
    // the bubble). undefined when not in read-only mode.
    const readonlyFeedbackOf = (messageId: string) =>
        props.readonlyFeedback ? getMessageFeedback(messageId) : undefined
    const feedbackVote = (messageId: string) =>
        readonlyFeedbackOf(messageId)?.vote
    // The real reaction glyph, shown ONLY for unmapped reactions (no vote):
    // 👍/❤️/👎 keep the classic thumb icon, while e.g. WhatsApp "😍" shows the
    // actual emoji. Platforms that send a type word (Teams "like") have a vote
    // and therefore use the icon too.
    const feedbackEmoji = (messageId: string) => {
        const feedback = readonlyFeedbackOf(messageId)
        if (feedback?.vote) {
            return undefined
        }
        const raw = feedback?.metadata?.reactionEmoji
        return typeof raw === 'string' && /\p{Extended_Pictographic}/u.test(raw)
            ? raw
            : undefined
    }
    const hasReaction = (messageId: string) =>
        Boolean(feedbackVote(messageId) || feedbackEmoji(messageId))
    const reactionToneClass = (messageId: string) => {
        // A real emoji carries its own colour → neutral pill. Only the
        // thumb-icon fallback uses the sentiment colour.
        if (feedbackEmoji(messageId)) {
            return 'pk-chatbot-message__reaction--neutral'
        }
        const vote = feedbackVote(messageId)
        if (vote === 'upvote') {
            return 'pk-chatbot-message__reaction--positive'
        }
        if (vote === 'downvote') {
            return 'pk-chatbot-message__reaction--negative'
        }
        return 'pk-chatbot-message__reaction--neutral'
    }
    const reactionTitle = (messageId: string) => {
        const vote = feedbackVote(messageId)
        if (vote === 'upvote') {
            return $t('hint.userFeedbackPositive')
        }
        if (vote === 'downvote') {
            return $t('hint.userFeedbackNegative')
        }
        return $t('hint.userFeedbackReaction')
    }

    // Index of the last text part: the reaction chip anchors to that bubble so
    // it renders once even when the message has several text parts.
    const lastTextPartIndex = (message: UIChatMessage) => {
        const parts = getMergedParts(message)
        for (let index = parts.length - 1; index >= 0; index -= 1) {
            if (isTextPart(parts[index])) {
                return index
            }
        }
        return -1
    }

    const activeMessage = computed(() => {
        return props.messages?.[props.messages.length - 1]
    })
    const activeMessageLastPart = computed(() => {
        const active = activeMessage.value
        if (!active) {
            return null
        }
        return active.parts[active.parts.length - 1]
    })

    const mergedPartsMap = computed(() => {
        const map = new Map<string, UIChatMessage['parts']>()
        for (const message of props.messages ?? []) {
            if (message.role === 'assistant') {
                map.set(message.id, mergeConsecutiveTextParts(message.parts))
            }
        }
        return map
    })
    const getMergedParts = (message: UIChatMessage) => {
        return mergedPartsMap.value.get(message.id) ?? message.parts
    }
    const isLoading = computed(() => {
        return props.status === 'submitted' || props.status === 'streaming'
    })
    const isError = computed(() => props.status === 'error')

    /**
     * The spoken wait shows only while nothing else is working: the wait
     * survives the hang-up (the answer can be written minutes later) and the
     * caller can type meanwhile, and two working indicators stacked would say
     * the same thing twice. It comes back when the written turn is over.
     */
    const isSpokenWaitVisible = computed(
        () => props.awaitingSpokenAnswer === true && !isLoading.value,
    )

    /**
     * The wait indicator closes the feed, so it carries the anchor spacer
     * itself, the way the streaming assistant message does. Rendering the
     * standalone spacer as well would stack the two and push the question
     * above the top of the content area, out of view.
     *
     * Only when it is the last thing in the feed: an error card, or an
     * assistant answer already landed, keeps the spacer where it was.
     */
    const isSpokenWaitAnchored = computed(
        () =>
            isSpokenWaitVisible.value &&
            activeMessage.value?.role === 'user' &&
            !isError.value,
    )

    const activeMessageLastPartLabel = computed(() => {
        const part = activeMessageLastPart.value
        return getToolPartLabel($t, part)
    })
    const activeMessageLastPartIcon = computed(() => {
        const part = activeMessageLastPart.value
        return getPartIcon(part)
    })

    const getMessageIndexById = (messageId: string) => {
        return props.messages?.findIndex((m) => m.id === messageId)
    }
    const isLastMessage = (index?: number) => {
        if (index === undefined || !props.messages) {
            return false
        }
        return index === props.messages.length - 1
    }
    const isMessageRegenerateButtonVisible = (index: number) => {
        return (
            isLastMessage(index) &&
            props.status === 'ready' &&
            !!props.actions?.includes('regenerate')
        )
    }
    // On the last message the actions appear only once the answer is ready;
    // on previous messages they are always available.
    const isActionButtonVisible = (
        action: ChatMessageActions,
        index: number,
    ) => {
        return (
            (!isLastMessage(index) || props.status === 'ready') &&
            !!props.actions?.includes(action)
        )
    }
    const isLastTextPart = (message: UIChatMessage) => {
        const lastPart = message.parts[message.parts.length - 1]
        return isTextPart(lastPart)
    }
    const messageHasSteps = (message: UIChatMessage) =>
        message.parts.some((part) => isReasoningPart(part) || isToolPart(part))
    // The extended steps timeline replaces the compact activity indicator as
    // soon as the message has at least one step to show.
    const showStepsTimeline = (message: UIChatMessage) =>
        Boolean(props.showExtendedSteps) &&
        isAssistant(message) &&
        messageHasSteps(message)
    const isAssistant = (message: UIChatMessage) => message.role === 'assistant'
    const isUser = (message: UIChatMessage) => message.role === 'user'
    const getPreviousAssistantMessage = (index: number) => {
        return props.messages
            ?.slice(0, index)
            .reverse()
            .find((message) => isAssistant(message))
    }
    const getNextAssistantMessage = (index: number) => {
        return props.messages
            ?.slice(index + 1)
            .find((message) => isAssistant(message))
    }
    // Returns the date to show in the day divider before the message at
    // `index`, or undefined when the surrounding assistant messages belong
    // to the same (local) day.
    const getDividerDate = (index: number) => {
        const previousCreatedAt =
            getPreviousAssistantMessage(index)?.metadata?.createdAt
        const nextCreatedAt =
            getNextAssistantMessage(index)?.metadata?.createdAt
        if (!previousCreatedAt || !nextCreatedAt) {
            return undefined
        }
        const previousDate = new Date(previousCreatedAt)
        const nextDate = new Date(nextCreatedAt)
        return previousDate.toDateString() !== nextDate.toDateString()
            ? nextDate
            : undefined
    }
    const showMessageFooter = (message: UIChatMessage, index: number) => {
        if (index === 0 || message.role !== 'assistant') {
            return false
        }
        return Boolean(
            props.actions?.length ||
            (props.showMessageDateTime &&
                message.metadata?.createdAt !== undefined) ||
            message.metadata?.completedAt !== undefined ||
            (props.showMessageTokensCount &&
                message.metadata?.totalTokens !== undefined),
        )
    }
    // Hover-only metadata (time + duration), shown when the fixed variant
    // (showMessageDateTime) is off.
    const formatMessageTime = (createdAt: number) => {
        const date = new Date(createdAt)
        const isToday = date.toDateString() === new Date().toDateString()
        return $d(date, isToday ? 'time' : 'date-time')
    }
    const getMessageDuration = (message: UIChatMessage) => {
        const { createdAt, completedAt } = message.metadata ?? {}
        if (!createdAt || !completedAt) {
            return undefined
        }
        return formatDuration($n, completedAt - createdAt)
    }
    // feedback message auto scroll
    const feedbackMessageEl =
        useTemplateRef<InstanceType<typeof PkChatbotFeedbackForm>[]>(
            'feedbackMessageEl',
        )
    watch(
        () => props.feedbackMessageId,
        async () => {
            await nextTick()
            if (!props.feedbackMessageId) {
                return
            }
            const el = feedbackMessageEl.value?.[0]?.$el
            if (!el) {
                return
            }
            if (isLastMessage(getMessageIndexById(props.feedbackMessageId))) {
                scrollToBottom()
                return
            }
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
    )
</script>

<template>
    <div
        ref="scrollEl"
        class="pk-chatbot-messages"
        :class="{ 'pk-chatbot-messages--settling': isSpacerSettling }"
        role="log"
        aria-live="polite"
        aria-relevant="additions text"
        :aria-busy="isLoading"
        :style="{
            '--chatbot-main-color': mainColor,
            '--chatbot-contrast-color': contrastColor,
        }"
        @scroll="onScroll">
        <!-- First in flow (content top): its absolutely-positioned children
         must never inflate the scrollable overflow past the real content -->
        <div
            v-if="showScrollToBottom && outlineItems.length > 1"
            class="pk-chatbot-messages__outline">
            <PkChatbotOutlineRail
                :items="outlineItems"
                :active-id="activeOutlineId"
                @select="scrollToUserMessage" />
        </div>
        <div ref="wrapperEl" class="pk-chatbot-messages__wrapper">
            <template v-for="(message, index) in messages" :key="message.id">
                <div
                    v-if="isUser(message) && getDividerDate(index)"
                    class="pk-chatbot-divider">
                    <span class="pk-chatbot-divider__label">
                        {{ $d(getDividerDate(index)!, 'short') }}
                    </span>
                </div>
                <div
                    v-if="
                        message.parts.length ||
                        (isLastMessage(index) &&
                            isLoading &&
                            isAssistant(message))
                    "
                    class="pk-chatbot-message"
                    :class="[
                        `pk-chatbot-message--${message.role}`,
                        {
                            'pk-chatbot-message--loading':
                                isLoading &&
                                isLastMessage(index) &&
                                isAssistant(message),
                        },
                    ]"
                    :style="{
                        minHeight:
                            isLastMessage(index) &&
                            isAssistant(message) &&
                            !isError
                                ? `${height}px`
                                : undefined,
                    }">
                    <transition appear name="pk-chatbot-part">
                        <PkChatbotSteps
                            v-if="showStepsTimeline(message)"
                            :message
                            :is-streaming="isLoading && isLastMessage(index)"
                            :is-dark />
                    </transition>
                    <template
                        v-for="(part, partIndex) in getMergedParts(message)"
                        :key="partIndex">
                        <transition
                            v-if="isTextPart(part) && part.text.trim()"
                            appear
                            name="pk-chatbot-part">
                            <div class="pk-chatbot-message__text">
                                <slot
                                    v-bind="{
                                        message,
                                        part,
                                        index,
                                        isLoading,
                                    }"
                                    name="text">
                                    <PkStreamingMarkdown
                                        v-if="isAssistant(message)"
                                        class="wysiwyg"
                                        :markdown="part.text"
                                        :is-dark
                                        :loading="
                                            isLastMessage(index) &&
                                            status === 'streaming'
                                        " />
                                    <template v-else>
                                        {{ part.text }}
                                    </template>
                                </slot>
                                <!-- Read-only reaction chip overlapping the
                                     bubble corner, like a messaging app. Hidden
                                     when a comment is shown: the callout below
                                     then carries the reaction glyph instead. -->
                                <span
                                    v-if="
                                        hasReaction(message.id) &&
                                        !getMessageFeedback(message.id)
                                            ?.comment &&
                                        partIndex === lastTextPartIndex(message)
                                    "
                                    class="pk-chatbot-message__reaction"
                                    :class="reactionToneClass(message.id)"
                                    :title="reactionTitle(message.id)">
                                    <span
                                        v-if="feedbackEmoji(message.id)"
                                        class="pk-chatbot-message__reaction-emoji">
                                        {{ feedbackEmoji(message.id) }}
                                    </span>
                                    <VvIcon
                                        v-else
                                        :name="
                                            feedbackVote(message.id) ===
                                            'downvote'
                                                ? 'ri:thumb-down-fill'
                                                : 'ri:thumb-up-fill'
                                        " />
                                </span>
                            </div>
                        </transition>
                        <transition
                            v-else-if="isFilePart(part)"
                            appear
                            name="pk-chatbot-part">
                            <PkChatbotFilePreview
                                :media-type="part.mediaType"
                                :url="part.url"
                                :filename="part.filename"
                                :size="getFilePartMedia(part)?.bytesCount" />
                        </transition>
                        <!-- Anything asking the user for consent (a suspended
                             tool call or a `requestConfirm`) shares one card,
                             ahead of the per-tool components. An approval stays
                             answerable only in the trailing message: the backend
                             closes the abandoned ones as denied, while a
                             `requestConfirm` can still be resolved later. -->
                        <transition
                            v-else-if="
                                isConfirmationCardPart(part) &&
                                !isStreamingPart(part)
                            "
                            appear
                            name="pk-chatbot-part">
                            <!-- Key WITHOUT the part state: the card must stay
                                 mounted across the approval lifecycle so its
                                 inner transitions play once; remounting per
                                 state replays the appear animation on every
                                 hop (requested → responded → output). -->
                            <PkToolApprovalCard
                                :key="`approval-${partIndex}`"
                                :part
                                :actionable="
                                    interactive === true &&
                                    (isToolConfirmPart(part) ||
                                        isLastMessage(index))
                                "
                                :call-verdict="
                                    confirmVerdictsOnCall?.get(
                                        (part as { toolCallId?: string })
                                            .toolCallId ?? '',
                                    )
                                "
                                @respond="onCardRespond(part, $event)" />
                        </transition>
                        <transition
                            v-else-if="
                                isToolPart(part) &&
                                !isStreamingPart(part) &&
                                (toolComponentMap[getToolPartName(part)] ||
                                    slots[part.type] ||
                                    showAllMessageParts)
                            "
                            appear
                            name="pk-chatbot-part">
                            <component
                                :is="toolComponentMap[getToolPartName(part)]"
                                v-if="
                                    !slots[part.type] &&
                                    toolComponentMap[getToolPartName(part)]
                                "
                                :key="`component-${partIndex}-${getPartState(part)}`"
                                :part />
                            <slot
                                v-else
                                :name="part.type"
                                v-bind="{
                                    message,
                                    part,
                                    index,
                                    isLoading,
                                }">
                                <div
                                    v-if="showAllMessageParts"
                                    class="pk-chatbot-message__loading-info">
                                    <div class="flex gap-8 items-center">
                                        <VvIcon
                                            v-if="getPartIcon(part)"
                                            :name="getPartIcon(part)!"
                                            class="shrink-0" />
                                        <code class="font-mono rounded text-12">
                                            {{ toKebabCase(part.type) }}
                                        </code>
                                    </div>
                                </div>
                            </slot>
                        </transition>
                        <!-- Read-only comment: anchored right after the message
                             text (same bubble as the reaction chip) so the
                             feedback stays attached to the message body. -->
                        <transition mode="out-in">
                            <PkChatbotMessageFeedback
                                v-if="
                                    partIndex === lastTextPartIndex(message) &&
                                    readonlyFeedback &&
                                    getMessageFeedback(message.id)?.comment
                                "
                                :feedback="getMessageFeedback(message.id)!" />
                        </transition>
                    </template>
                    <transition name="fade-in" mode="out-in">
                        <div
                            v-if="
                                isLoading &&
                                isLastMessage(index) &&
                                !isLastTextPart(message) &&
                                isAssistant(message) &&
                                !showStepsTimeline(message)
                            "
                            :key="`loading-info-${message.id}`"
                            class="pk-chatbot-message__loading-info">
                            <div class="flex gap-8 items-center">
                                <VvIcon
                                    name="line-md:loading-loop"
                                    class="shrink-0" />
                                <transition mode="out-in">
                                    <VvIcon
                                        v-if="activeMessageLastPartIcon"
                                        :key="activeMessageLastPartIcon"
                                        class="shrink-0"
                                        :name="activeMessageLastPartIcon" />
                                </transition>
                                <transition mode="out-in">
                                    <span
                                        v-if="activeMessageLastPartLabel"
                                        :key="activeMessageLastPartLabel"
                                        class="text-12">
                                        {{ activeMessageLastPartLabel }}
                                    </span>
                                </transition>
                            </div>
                            <transition name="fade-in" mode="out-in">
                                <PkStreamingMarkdownAutoscroll
                                    v-if="
                                        activeMessageLastPart &&
                                        'text' in activeMessageLastPart &&
                                        activeMessageLastPart.text.trim()
                                    "
                                    :markdown="activeMessageLastPart.text"
                                    :is-dark
                                    inner-class="wysiwyg"
                                    class="border border-surface-3 rounded-md p-4 mt-8 bg-surface-1 max-h-64 text-12 w-full" />
                            </transition>
                        </div>
                    </transition>
                    <transition mode="out-in">
                        <div
                            v-if="showMessageFooter(message, index)"
                            class="pk-chatbot-message__footer">
                            <VvButtonGroup
                                modifiers="compact"
                                class="mr-auto shrink-0">
                                <transition mode="out-in">
                                    <VvButton
                                        v-if="
                                            isMessageRegenerateButtonVisible(
                                                index,
                                            )
                                        "
                                        icon="ri:reset-right-line"
                                        modifiers="action-quiet-small"
                                        :title="$t('action.regenerate')"
                                        :aria-label="$t('action.regenerate')"
                                        @click.stop="$emit('regenerate')" />
                                </transition>
                                <transition mode="out-in">
                                    <VvButton
                                        v-if="
                                            isActionButtonVisible(
                                                'show-info',
                                                index,
                                            )
                                        "
                                        icon="ri:information-line"
                                        :title="$t('action.getMoreInfo')"
                                        :aria-label="$t('action.getMoreInfo')"
                                        modifiers="action-quiet-small"
                                        @click.stop="
                                            $emit('show-info', message)
                                        " />
                                </transition>
                                <transition mode="out-in">
                                    <VvButton
                                        v-if="
                                            isActionButtonVisible(
                                                'revise',
                                                index,
                                            )
                                        "
                                        :icon="
                                            isRevised(message.id)
                                                ? 'ri:file-edit-fill'
                                                : 'ri:file-edit-line'
                                        "
                                        :title="
                                            isRevised(message.id)
                                                ? $t('action.editRevise')
                                                : $t('action.createRevise')
                                        "
                                        :aria-label="
                                            isRevised(message.id)
                                                ? $t('action.editRevise')
                                                : $t('action.createRevise')
                                        "
                                        modifiers="action-quiet-small"
                                        :class="{
                                            'text-brand': isRevised(message.id),
                                        }"
                                        @click.stop="
                                            $emit('revise', message)
                                        " />
                                </transition>
                                <transition mode="out-in">
                                    <VvButton
                                        v-if="
                                            isFeedbackButtonVisible(
                                                'upvote',
                                                index,
                                            )
                                        "
                                        :icon="
                                            getMessageFeedback(message.id)
                                                ?.vote === 'upvote'
                                                ? 'ri:thumb-up-fill'
                                                : 'ri:thumb-up-line'
                                        "
                                        :title="$t('action.upvote')"
                                        :aria-label="$t('action.upvote')"
                                        modifiers="action-quiet-small"
                                        @click.stop="
                                            $emit('upvote', message)
                                        " />
                                </transition>
                                <transition mode="out-in">
                                    <VvButton
                                        v-if="
                                            isFeedbackButtonVisible(
                                                'downvote',
                                                index,
                                            )
                                        "
                                        :icon="
                                            getMessageFeedback(message.id)
                                                ?.vote === 'downvote'
                                                ? 'ri:thumb-down-fill'
                                                : 'ri:thumb-down-line'
                                        "
                                        :title="$t('action.downvote')"
                                        :aria-label="$t('action.downvote')"
                                        modifiers="action-quiet-small"
                                        @click.stop="
                                            $emit('downvote', message)
                                        " />
                                </transition>
                                <transition mode="out-in">
                                    <VvButton
                                        v-if="
                                            isFeedbackButtonVisible(
                                                'feedback',
                                                index,
                                            )
                                        "
                                        :icon="
                                            getMessageFeedback(message.id)
                                                ?.comment
                                                ? 'ri:feedback-fill'
                                                : 'ri:feedback-line'
                                        "
                                        :title="$t('action.feedback')"
                                        :aria-label="$t('action.feedback')"
                                        modifiers="action-quiet-small"
                                        @click.stop="
                                            $emit('feedback', message)
                                        " />
                                </transition>
                            </VvButtonGroup>
                            <div
                                v-if="
                                    !showMessageDateTime &&
                                    message.metadata?.createdAt &&
                                    message.metadata?.completedAt
                                "
                                class="pk-chatbot-message__hover-meta">
                                <time
                                    :datetime="
                                        new Date(
                                            message.metadata.createdAt,
                                        ).toISOString()
                                    "
                                    :title="
                                        $d(
                                            new Date(
                                                message.metadata.createdAt,
                                            ),
                                            'date-time',
                                        )
                                    "
                                    class="flex items-center gap-4 min-w-0">
                                    <VvIcon
                                        name="ri:time-line"
                                        class="shrink-0" />
                                    <span class="truncate">{{
                                        formatMessageTime(
                                            message.metadata.createdAt,
                                        )
                                    }}</span>
                                </time>
                                <span
                                    v-if="getMessageDuration(message)"
                                    class="flex items-center gap-4 min-w-0">
                                    <VvIcon
                                        name="ri:hourglass-line"
                                        class="shrink-0" />
                                    <span class="truncate">{{
                                        getMessageDuration(message)
                                    }}</span>
                                </span>
                            </div>
                            <span
                                v-if="
                                    showMessageTokensCount &&
                                    message.metadata?.totalTokens
                                "
                                class="flex items-center gap-4 text-12 text-word-3">
                                <VvIcon
                                    name="ri:ai-generate-2-line"
                                    class="shrink-0" />
                                <span class="truncate"
                                    >{{
                                        $n(
                                            message.metadata.totalTokens,
                                            'integer',
                                        )
                                    }}
                                    {{ $t('label.tokens') }}
                                </span>
                            </span>
                            <time
                                v-if="
                                    showMessageDateTime &&
                                    message.metadata?.createdAt
                                "
                                :datetime="
                                    new Date(
                                        message.metadata?.createdAt,
                                    ).toISOString()
                                "
                                class="flex items-center gap-4 text-12 text-word-3">
                                <VvIcon name="ri:time-line" class="shrink-0" />
                                <span class="truncate">{{
                                    $d(
                                        new Date(message.metadata?.createdAt),
                                        'date-time',
                                    )
                                }}</span>
                            </time>
                            <div
                                v-if="
                                    showMessageDateTime &&
                                    message?.metadata?.completedAt &&
                                    message?.metadata?.createdAt
                                "
                                class="flex items-center gap-4 text-12 text-word-3">
                                <VvIcon
                                    name="ri:hourglass-line"
                                    class="shrink-0" />
                                <PkRelativeTime
                                    class="truncate"
                                    :date="message.metadata?.createdAt"
                                    :end-date="message.metadata?.completedAt" />
                            </div>
                        </div>
                    </transition>
                    <transition mode="out-in">
                        <PkChatbotFeedbackForm
                            v-if="message.id === feedbackMessageId"
                            ref="feedbackMessageEl"
                            :loading="feedbackLoading"
                            :submitted="feedbackSubmitted"
                            :error="feedbackError"
                            @submit="$emit('feedback-submit', $event)"
                            @close="$emit('feedback-close')" />
                    </transition>
                </div>
            </template>
            <!-- A spoken turn streams to the provider, not here: nothing in
                 this list moves between the question and the answer landing.
                 Its own indicator, because the status-driven one hangs off an
                 assistant message the SDK never creates for a call. When to
                 show it and when it carries the anchor spacer: see
                 `isSpokenWaitVisible` and `isSpokenWaitAnchored`. -->
            <transition appear name="pk-chatbot-part">
                <div
                    v-if="isSpokenWaitVisible"
                    class="pk-chatbot-message pk-chatbot-message--assistant pk-chatbot-message--loading"
                    :style="{
                        minHeight: isSpokenWaitAnchored
                            ? `${height}px`
                            : undefined,
                    }">
                    <div class="pk-chatbot-message__loading-info">
                        <div class="flex gap-8 items-center">
                            <VvIcon
                                name="line-md:loading-loop"
                                class="shrink-0" />
                            <span class="text-12">
                                {{ $t('label.answeringOnTheCall') }}
                            </span>
                        </div>
                    </div>
                </div>
            </transition>
            <transition appear name="pk-chatbot-part">
                <PkChatbotError
                    v-if="isError"
                    :error
                    @retry="$emit('auto-retry')"
                    @reset="$emit('reset-chat')" />
            </transition>
            <div
                v-if="
                    (activeMessage?.role === 'user' && !isSpokenWaitAnchored) ||
                    isError
                "
                class="pk-chatbot-messages__spacer"
                :style="{ minHeight: `${height}px` }"></div>
        </div>
        <Transition name="pk-chatbot-messages-fab">
            <button
                v-if="showScrollToBottom && (userScrolledUp || !isAtBottom)"
                type="button"
                class="pk-chatbot-messages__scroll-to-bottom"
                :title="$t('action.scrollToBottom')"
                :aria-label="$t('action.scrollToBottom')"
                @click="onScrollToBottomClick">
                <VvIcon name="ri:arrow-down-line" />
            </button>
        </Transition>
    </div>
</template>

<style lang="scss">
    .pk-chatbot-messages {
        overflow-y: auto;
        flex: 1;
        min-width: 0;
        font-size: var(--text-14);

        scrollbar-width: thin;
        scrollbar-color: var(--color-word-5) var(--color-surface-1);

        &__wrapper {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-16);
            min-width: 0;
        }

        // Conversation outline rail: sticky shell pinned to the vertical
        // middle of the scroll area, at the right edge. Zero-sized and
        // first in flow so neither it nor its absolutely-positioned pieces
        // inflate scrollHeight. Desktop only, like ChatGPT: in the widget and
        // the panel the chat is as wide on a phone as on a desktop, so the
        // viewport is the only thing that tells them apart. The fullscreen
        // layout measures the chat itself instead (PkChatbot).
        &__outline {
            display: none;
            position: sticky;
            top: 50%;
            z-index: 2;
            flex: none;
            align-self: flex-end;
            height: 0;

            @include media-breakpoint-up('md', $breakpoints) {
                display: block;
            }

            .pk-chatbot-outline {
                position: absolute;
                top: 0;
                right: 0;
                transform: translateY(-50%);
            }
        }

        // Placeholder while the answer has not started yet: settles like
        // the message spacer when released. Only the release is animated
        // (`--settling`): animating the growth would keep `scrollHeight`
        // behind while the anchor scroll reads it, landing short of the
        // anchor with no second chance to catch up.
        &--settling {
            .pk-chatbot-messages__spacer,
            .pk-chatbot-message {
                transition: min-height 300ms var(--ease-out);

                @media (prefers-reduced-motion: reduce) {
                    transition: none;
                }
            }
        }

        // Floating "back to bottom" button, sticky inside the scroll area
        &__scroll-to-bottom {
            position: sticky;
            bottom: var(--spacing-8);
            z-index: 2;
            display: flex;
            align-items: center;
            justify-content: center;
            // The scroll container is a flex column: without this the
            // overflowing content shrinks the button, ignoring its height
            flex: none;
            width: var(--spacing-36);
            height: var(--spacing-36);
            // Net-zero layout contribution: mounting/unmounting the button
            // must not change scrollHeight, or reaching the bottom (which
            // hides it) would clamp scrollTop and cause a visible jump
            margin-block-start: calc(-1 * var(--spacing-36));
            margin-inline: auto;
            border: 1px solid var(--color-surface-3);
            border-radius: var(--rounded-full);
            background-color: var(--color-surface-1);
            box-shadow: var(--shadow-md);
            color: var(--color-word-1);
            cursor: pointer;
            transition: var(--transition-colors);

            &:hover {
                background-color: var(--color-surface-2);
            }
        }
    }

    .pk-chatbot-messages-fab-enter-active,
    .pk-chatbot-messages-fab-leave-active {
        transition: opacity 150ms var(--ease-out);
    }

    .pk-chatbot-messages-fab-enter-from,
    .pk-chatbot-messages-fab-leave-to {
        opacity: 0;
    }

    @media (prefers-reduced-motion: reduce) {
        .pk-chatbot-messages-fab-enter-active,
        .pk-chatbot-messages-fab-leave-active {
            transition: none;
        }
    }

    .pk-chatbot-divider {
        position: relative;
        border-bottom: 1px solid var(--color-surface-3);

        &__label {
            position: absolute;
            padding-inline: var(--spacing-8);
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-color: var(--color-surface);
            color: var(--color-word-4);
            font-size: var(--text-12);
        }
    }

    .pk-chatbot-message {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
        min-width: 0;
        align-items: flex-start;
        // `clip` instead of `hidden` so the reaction chip, which deliberately
        // overhangs the bubble's bottom edge, is not cut off. The margin has
        // to cover half the chip plus its shadow.
        overflow: clip;
        overflow-clip-margin: var(--spacing-16);
        max-width: 100%;
        // The anchor spacer (inline min-height on the last assistant message)
        // settles smoothly when released at the end of the stream: the
        // transition lives on `.pk-chatbot-messages--settling` above

        &__text {
            position: relative;
            color: var(--color-word-2);
            background-color: var(--color-surface);
            line-height: var(--leading-normal);
            border-width: var(--spacing-px);
            border-style: solid;
            border-color: var(--color-surface-3);
            padding: var(--spacing-sm);
            border-radius: var(--rounded-xl) var(--rounded-xl) var(--rounded-xl)
                0;
            max-width: 100%;
            text-overflow: ellipsis;
            // Same reason as the block above: the chip is absolutely
            // positioned against this bubble and hangs below its edge.
            overflow: clip;
            overflow-clip-margin: var(--spacing-16);
        }

        // Read-only feedback: a circular vote chip that overlaps the bubble's
        // bottom-right corner (messaging-app style). White ring lifts it off
        // the bubble; solid accent makes the rating unmistakable.
        &__reaction {
            position: absolute;
            right: var(--spacing-8);
            bottom: 0;
            transform: translateY(50%);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: var(--spacing-24);
            height: var(--spacing-24);
            border-radius: var(--rounded-full, 9999px);
            color: var(--color-white, #fff);
            font-size: var(--text-12);
            box-shadow: var(--shadow-md);

            &--positive {
                background-color: var(--color-success);
            }

            &--negative {
                background-color: var(--color-danger);
            }

            // Real emoji (e.g. WhatsApp): the glyph carries its own colour, so
            // the chip stays a neutral surface pill.
            &--neutral {
                background-color: var(--color-surface-1);
            }
        }

        &__reaction-emoji {
            // Smaller than the chip so the glyph doesn't touch the ring.
            font-size: var(--text-12);
            line-height: 1;
        }

        &__loading-info {
            line-height: var(--leading-normal);
            border-width: var(--spacing-px);
            border-style: solid;
            border-color: var(--color-surface-3);
            padding: var(--spacing-sm);
            border-radius: var(--rounded-xl) var(--rounded-xl) var(--rounded-xl)
                0;
            font-size: var(--text-12);
            color: var(--color-word-3);
            background-color: var(--color-surface);
        }

        &__footer {
            width: 100%;
            display: flex;
            gap: var(--spacing-8);
            align-items: center;
        }

        &__hover-meta {
            display: flex;
            min-width: 0;
            align-items: center;
            gap: var(--spacing-8);
            font-size: var(--text-12);
            color: var(--color-word-3);
            white-space: nowrap;
            font-variant-numeric: tabular-nums;
            opacity: 0;
            transition: opacity 0.2s ease;
        }

        &:hover &__hover-meta,
        &:focus-within &__hover-meta {
            opacity: 1;
        }

        // Hover doesn't exist on touch devices: keep the metadata visible
        @media (hover: none) {
            &__hover-meta {
                opacity: 1;
            }
        }

        &--user {
            align-items: flex-end;

            .pk-chatbot-message__text {
                background-color: var(
                    --chatbot-main-color,
                    var(--color-surface-1)
                );
                border-color: var(--chatbot-main-color, var(--color-surface-3));
                color: var(--chatbot-contrast-color, var(--color-word-1));
                border-radius: var(--rounded-xl) var(--rounded-xl) 0
                    var(--rounded-xl);
            }
        }

        &--assistant {
            width: 100%;
            gap: var(--spacing-16);

            .pk-chatbot-message__text {
                width: 100%;
            }
        }

        &--system {
            align-items: flex-end;

            .pk-chatbot-message__text {
                display: flex;
                gap: var(--spacing-8);
                font-size: var(--text-12);
                padding: var(--spacing-8) var(--spacing-sm);
                align-items: center;
                border-color: var(--chatbot-main-color, var(--color-surface-3));
                color: var(--chatbot-main-color, var(--color-word-1));
                border-radius: var(--rounded-xl) var(--rounded-xl) 0
                    var(--rounded-xl);
            }
        }
    }

    .pk-chatbot-part-enter-active {
        transition:
            opacity 0.25s ease,
            transform 0.25s ease;
    }

    .pk-chatbot-part-enter-from {
        opacity: 0;
        transform: translateY(8px);
    }

    @media (prefers-reduced-motion: reduce) {
        .pk-chatbot-part-enter-active {
            transition: none;
        }

        .pk-chatbot-part-enter-from {
            transform: none;
        }
    }
</style>
